// components/doctor-dashboard/DoctorTodaysAppointments.jsx
import React, { useMemo, useState } from "react";
import { Calendar, User, Clock, FileText, Edit3, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { toDate, isSameLocalDay, parseLocalTimeToMinutes } from "../../utils/dateUtils";

/* --- Helpers --- */
const defaultGetStatusColor = (status) => {
  switch (status) {
    case "confirmed": return { bg: "#dcfce7", fg: "#166534" };
    case "pending":   return { bg: "#fef3c7", fg: "#92400e" };
    case "completed": return { bg: "#dbeafe", fg: "#1e40af" };
    case "cancelled": return { bg: "#fee2e2", fg: "#991b1b" };
    default:          return { bg: "#f3f4f6", fg: "#374151" };
  }
};

const isoOf = (d) => {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(`${d}T00:00:00`) : d;
  const y = dt.getFullYear();
  const m = `${dt.getMonth() + 1}`.padStart(2, "0");
  const day = `${dt.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const clampDate = (d, min, max) => {
  const t = new Date(isoOf(d));
  const lo = min ? new Date(isoOf(min)) : null;
  const hi = max ? new Date(isoOf(max)) : null;
  if (lo && t < lo) return lo;
  if (hi && t > hi) return hi;
  return t;
};

const shiftDays = (d, delta) => {
  const nd = new Date(isoOf(d));
  nd.setDate(nd.getDate() + delta);
  return nd;
};

const prettyTime = (timeString) => {
  if (!timeString) return "Time TBD";
  try {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch { return timeString; }
};

const prettyRequested = (date) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  }).format(date);

const truncate = (text = "", max = 120) =>
  text.length > max ? text.slice(0, max).trimEnd() + "…" : text;

/* --- Component --- */
const DoctorTodaysAppointments = ({
  bookings = [],
  onOpenMedicalNotes,
  getStatusColor = (s) => {
    const c = defaultGetStatusColor(s);
    return { backgroundColor: c.bg, color: c.fg };
  },
  dense = true,

  /** New props */
  initialDate,              // Date | "YYYY-MM-DD" (default: today)
  minDate,                  // Date | "YYYY-MM-DD" (default: none)
  maxDate,                  // Date | "YYYY-MM-DD" (default: today)
  onDateChange,             // (date: Date) => void
}) => {
  const today = useMemo(() => new Date(), []);
  const todayISO = isoOf(today);

  const resolvedMax = maxDate ? isoOf(maxDate) : todayISO;
  const [selectedDate, setSelectedDate] = useState(() => {
    const base = initialDate ? initialDate : today;
    return clampDate(base, minDate, resolvedMax);
  });

  const selectedISO = isoOf(selectedDate);
  const canGoNext = selectedISO < resolvedMax; // prevent going into future
  const canGoPrev = !minDate || isoOf(selectedDate) > isoOf(minDate);

  const changeDate = (d) => {
    const clamped = clampDate(d, minDate, resolvedMax);
    setSelectedDate(clamped);
    onDateChange?.(clamped);
  };

  const goPrev = () => changeDate(shiftDays(selectedDate, -1));
  const goNext = () => {
    if (!canGoNext) return;
    changeDate(shiftDays(selectedDate, 1));
  };
  const onPickDate = (e) => changeDate(e.target.value);

  const selectedList = useMemo(() => {
    const list = bookings.filter((b) => {
      const sched = toDate(b.scheduledDate);
      return b.status === "confirmed" && sched && isSameLocalDay(sched, selectedDate);
    });
    return list.sort((a, b) => {
      const ta = parseLocalTimeToMinutes(a.scheduledTime);
      const tb = parseLocalTimeToMinutes(b.scheduledTime);
      if (ta !== tb) return ta - tb;
      const ca = toDate(a.createdAt) || new Date(0);
      const cb = toDate(b.createdAt) || new Date(0);
      return ca - cb;
    });
  }, [bookings, selectedDate]);

  // expand/collapse
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const MAX_REASON = dense ? 80 : 120;
  const MAX_NOTES  = dense ? 110 : 160;

  return (
    <div
      className={`ta-root ${dense ? "ta--dense" : ""}`}
      style={{
        background: "transparent",
        border: "none",
        boxShadow: "none",
        borderRadius: 0,
        overflow: "hidden",
        marginBottom: 0,
        width: "100%",
      }}
    >
      <style>{`
        .ta-root {
          --pad-header: 16px;
          --title-fs: 16px;
          --sub-fs: 12px;
          --scroll-max: min(60vh, 640px);
          --item-gap: 12px;
          --item-pad: 12px 14px;
          --item-radius: 10px;
          --avatar: 40px;
          --avatar-radius: 10px;
          --name-fs: 14.5px;
          --chip-fs: 12px;
          --chip-pad: 5px 9px;
          --badge-fs: 12px;
          --badge-pad: 4px 8px;
          --btn-fs: 12.5px;
          --btn-pad: 8px 12px;
          --timeline-left: 30px;
          --timeline-w: 2px;
          --list-pad: 10px 0 6px 0;
        }
        .ta--dense {
          --pad-header: 12px;
          --title-fs: 15px;
          --sub-fs: 11.5px;
          --scroll-max: min(76vh, 750px);
          --item-gap: 10px;
          --item-pad: 10px 12px;
          --item-radius: 10px;
          --avatar: 36px;
          --avatar-radius: 8px;
          --name-fs: 14px;
          --chip-fs: 11.5px;
          --chip-pad: 4px 8px;
          --badge-fs: 11.5px;
          --badge-pad: 4px 8px;
          --btn-fs: 12px;
          --btn-pad: 7px 10px;
          --timeline-left: 28px;
          --list-pad: 8px 0 4px 0;
        }

        /* Header */
        .ta-header {
          position: sticky; top: 0; z-index: 1;
          padding: var(--pad-header);
          background: #fff;
          border-bottom: 1px solid #eee;
          display:flex; align-items:center; justify-content:space-between;
          gap: 8px;
        }
        .ta-title { display:flex; align-items:center; gap:8px; margin:0; font-size: var(--title-fs); font-weight: 700; color:#0f172a; }
        .ta-sub   { font-size: var(--sub-fs); color:#657084; font-weight:600; background:#f3f4f6; border:1px solid #eee; padding:4px 10px; border-radius:999px; }

        .ta-controls { display:flex; align-items:center; gap:8px; }
        .ta-navbtn {
          display:grid; place-items:center;
          width:28px; height:28px; border-radius:8px; border:1px solid #e5e7eb;
          background:#f9fafb; color:#374151; cursor:pointer;
        }
        .ta-navbtn:disabled { opacity:.45; cursor:not-allowed; }
        .ta-date {
          font: inherit; font-weight:700; color:#0f172a;
          background:#fff; border:1px solid #e5e7eb; border-radius:8px;
          padding:6px 8px;
        }

        /* SCROLLER */
        .ta-scroll { max-height: var(--scroll-max); overflow-y: auto; -webkit-overflow-scrolling: touch; padding-bottom: 0; }
        .ta-scroll::-webkit-scrollbar { width: 10px; }
        .ta-scroll::-webkit-scrollbar-thumb { background: rgba(100,116,139,.35); border-radius: 999px; border: 2px solid transparent; background-clip: padding-box; }

        .ta-list { position:relative; padding: var(--list-pad); padding-bottom: 4px; }
        .ta-item:last-child { margin-bottom: 0; }

        /* Timeline */
        .ta-timeline { position:absolute; left: var(--timeline-left); top:0; bottom:0; width: var(--timeline-w); background:#eaeaea; }

        /* Card item */
        .ta-item {
          position: relative; display:flex; gap: var(--item-gap); padding: var(--item-pad);
          margin-left: 16px; margin-right: 8px; margin-bottom: 10px;
          background: #fff;
          border: 1px solid #eaeaea; border-radius: var(--item-radius);
          transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
        }
        @media (hover:hover) and (pointer:fine){
          .ta-item:hover { transform: translateY(-1px); box-shadow: 0 8px 20px -14px rgba(0,0,0,.18); border-color:#e2e2e2; }
        }

        /* Pin + avatar */
        .ta-pin { position:absolute; left:-10px; top:14px; width: 18px; height: 18px; border-radius:999px; background:#fff; border: 2px solid #0a66c2; display:flex; align-items:center; justify-content:center; }
        .ta-avatar {
          width: var(--avatar); height: var(--avatar); flex: 0 0 var(--avatar);
          border-radius: var(--avatar-radius); display:flex; align-items:center; justify-content:center;
          background:#0a66c2; color:#fff;
        }

        .ta-main { min-width: 0; flex: 1 1 auto; }
        .ta-name { margin:0; color:#0f172a; font-weight:700; font-size: var(--name-fs); line-height:1.2 }

        .ta-meta { display:flex; flex-wrap:wrap; gap:8px; margin-top:6px; align-items:center; }
        .ta-chip { display:inline-flex; align-items:center; gap:6px; font-size: var(--chip-fs); font-weight:600; color:#334155; background:#f3f4f6; border:1px solid #eee; border-radius:999px; padding: var(--chip-pad); }
        .ta-badge { font-size: var(--badge-fs); padding: var(--badge-pad); border-radius: 999px; font-weight: 700; border:1px solid transparent; }

        .ta-notes { margin-top:8px; font-size:12.5px; color:#657084; background:#fafafa; border: 1px dashed #eaeaea; padding:8px 10px; border-radius:8px; }

        .ta-side { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .ta-btn { display:inline-flex; align-items:center; gap:6px; cursor:pointer; border:none; border-radius:8px; padding: var(--btn-pad); font-size: var(--btn-fs); font-weight:700; color:#fff; background: #10b981; transition: transform .15s ease, box-shadow .15s ease, filter .15s ease; }
        .ta-btn:hover { transform: translateY(-1px); }
        .ta-btn:focus { outline:none; box-shadow: 0 0 0 3px rgba(16,185,129,.25); }

        .ta-link { background: none; border: none; padding: 0; margin-left: 8px; color: #0a66c2; font-weight: 700; cursor: pointer; font-size: 12.5px; }
        .ta-link:hover { text-decoration: underline; }

        @media (max-width: 560px){
          .ta-item { margin-left: 10px; margin-right: 4px; }
          .ta-header { padding-left: 8px; padding-right: 8px; }
        }
      `}</style>

      {/* Header with date controls */}
      <div className="ta-header">
        <h2 className="ta-title">
          <Clock style={{ width: 18, height: 18, color: "#10b981" }} />
          Appointments
        </h2>

        <div className="ta-controls">
          <button className="ta-navbtn" onClick={goPrev} disabled={!canGoPrev} title="Previous day">
            <ChevronLeft size={16} />
          </button>

          <input
            type="date"
            className="ta-date"
            value={selectedISO}
            max={resolvedMax}
            min={minDate ? isoOf(minDate) : undefined}
            onChange={onPickDate}
          />

          <button className="ta-navbtn" onClick={goNext} disabled={!canGoNext} title="Next day">
            <ChevronRight size={16} />
          </button>

          <span className="ta-sub" style={{ marginLeft: 8 }}>
            {new Date(selectedISO + "T00:00:00").toLocaleDateString(undefined, {
              weekday: "long", month: "long", day: "numeric",
            })} • {selectedList.length} scheduled
          </span>
        </div>
      </div>

      {/* List */}
      <div className="ta-scroll">
        {selectedList.length === 0 ? (
          <div style={{ textAlign: "center", padding: dense ? "28px 12px" : "40px 16px" }}>
            <div
              style={{
                background: "#f3f4f6",
                border: "1px solid #eee",
                borderRadius: 12,
                padding: dense ? 14 : 18,
                display: "inline-block",
                marginBottom: 0,
              }}
            >
              <Calendar style={{ width: dense ? 36 : 42, height: dense ? 36 : 42, color: "#0a66c2" }} />
            </div>
            <h3 style={{ fontSize: dense ? 16 : 18, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>
              No appointments for this day
            </h3>
            <p style={{ color: "#657084", margin: 0 }}>
              Pick another date to review past appointments.
            </p>
          </div>
        ) : (
          <div className="ta-list">
            <div className="ta-timeline" />
            {selectedList.map((appointment) => {
              const { bg, fg } = defaultGetStatusColor(appointment.status);
              const id = appointment.id;
              const isOpen = expanded.has(id);

              const reasonText = appointment.reason || "—";
              const notesText  = appointment.notes || "";

              const reasonDisplay = isOpen ? reasonText : truncate(reasonText, MAX_REASON);
              const notesDisplay  = isOpen ? notesText  : truncate(notesText,  MAX_NOTES);

              const needsToggle =
                (reasonText && reasonText.length > MAX_REASON) ||
                (notesText && notesText.length > MAX_NOTES);

              return (
                <article key={id} className="ta-item">
                  <div className="ta-pin" aria-hidden>
                    <CheckCircle2 style={{ width: dense ? 10 : 12, height: dense ? 10 : 12, color: "#0a66c2" }} />
                  </div>

                  <div className="ta-avatar" aria-hidden>
                    <User style={{ width: dense ? 16 : 18, height: dense ? 16 : 18 }} />
                  </div>

                  <div className="ta-main">
                    <h3 className="ta-name">{appointment.patientName || "Unknown Patient"}</h3>

                    <div className="ta-meta">
                      <span className="ta-chip" title="Time">
                        <Clock style={{ width: dense ? 12 : 13, height: dense ? 12 : 13 }} />
                        {prettyTime(appointment.scheduledTime)}
                      </span>
                      <span className="ta-chip" title="Service">
                        {appointment.service || "Unknown Service"}
                      </span>
                      <span className="ta-badge" style={{ backgroundColor: bg, color: fg, borderColor: bg }}>
                        {appointment.status}
                      </span>
                    </div>

                    <div style={{ marginTop: 6, fontSize: dense ? 12.5 : 13, color: "#334155" }}>
                      <strong>Reason:</strong> {reasonDisplay}
                      {needsToggle && (
                        <button
                          type="button"
                          className="ta-link"
                          onClick={() => toggleExpanded(id)}
                          aria-expanded={isOpen}
                          aria-controls={`apt-${id}-details`}
                        >
                          {isOpen ? "Hide details" : "View details"}
                        </button>
                      )}
                    </div>

                    {notesText && (
                      <div id={`apt-${id}-details`} className="ta-notes">
                        <strong style={{ color: "#475569" }}>Notes:</strong> {notesDisplay}
                      </div>
                    )}

                    {appointment.createdAt && (
                      <p style={{ fontSize: 12, color: "#94a3b8", margin: "8px 0 0 0", display: "flex", alignItems: "center", gap: 6 }}>
                        <FileText style={{ width: 12, height: 12 }} />
                        Requested {prettyRequested(toDate(appointment.createdAt) || new Date())}
                      </p>
                    )}
                  </div>

                  <div className="ta-side">
                    <button
                      type="button"
                      className="ta-btn"
                      onClick={() => onOpenMedicalNotes?.(appointment)}
                    >
                      <Edit3 style={{ width: dense ? 12 : 13, height: dense ? 12 : 13 }} />
                      Medical Notes
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(DoctorTodaysAppointments);
